import type { ICer } from "@bbj-types/candidate/cer";
import { CERStatus } from "@bbj-types/candidate/cerstatus";
import {
  ExternalIntegrationStatusLabel,
  type IExternalIntegrationStatusLabelProps,
} from "@data-display/ExternalIntegrationStatusLabel";
import { RemoveRedEyeRounded } from "@mui/icons-material";
import { Chip, CircularProgress, IconButton, Stack, Typography } from "@mui/material";
import type { ChangeEvent } from "react";
import { ToSignPaperChip } from "./ToSignPaperChip";
import { CerButtons } from "./buttons";
import { chipProps } from "./constant";
import { useCerCard } from "./logics";
import { CerCardStyled, classes } from "./styles";

export interface IPaperCollapseCer {
  cer: Pick<ICer, "beneficiary" | "dates" | "document" | "status" | "signature" | "structure" | "externalIntegrations">;
  onEdit?: () => void;
  loadingUpload?: boolean;
  onDelete?: () => void;
  onLoadDocument?: (data: ChangeEvent<HTMLInputElement>) => Promise<void>;
  transferToDraft?: () => void;
  onErrorLinkClick?: IExternalIntegrationStatusLabelProps["onErrorLinkClick"];
  onRetry?: IExternalIntegrationStatusLabelProps["onRetry"];
}

export const CerCard = ({
  cer,
  onEdit,
  onDelete,
  loadingUpload,
  onLoadDocument,
  transferToDraft,
  onErrorLinkClick,
  onRetry,
}: IPaperCollapseCer) => {
  const {
    cerStartDate,
    cerEndDate,
    getPdf,
    pdfLoading,
    isCerDone,
    popoverAnchor,
    handlePopoverClose,
    handleChipClick,
  } = useCerCard(cer);

  return (
    <CerCardStyled className={classes.root}>
      <Stack
        direction="row"
        sx={{
          gap: 3,
          alignItems: "center",
        }}
      >
        <Stack>
          <Typography noWrap>{`CE du ${cerStartDate} au ${cerEndDate}`}</Typography>
          {cer.externalIntegrations?.map((integration) => (
            <ExternalIntegrationStatusLabel
              key={integration.provider}
              provider={integration.provider}
              status={integration.status}
              error={integration.error}
              onErrorLinkClick={onErrorLinkClick}
              onRetry={onRetry}
              variant="body2"
              sx={{ lineHeight: 0.5 }}
            />
          ))}
        </Stack>
        {isCerDone ? (
          <Chip label="Terminé" variant="outlined" className={classes.doneChip} size="small" />
        ) : (
          <>
            <Chip
              size="small"
              {...chipProps[cer.status]}
              {...(cer.status === CERStatus.TO_SIGN_PAPER ? { onClick: handleChipClick } : undefined)}
            />
            <ToSignPaperChip open={Boolean(popoverAnchor)} anchorEl={popoverAnchor} onClose={handlePopoverClose} />
          </>
        )}
      </Stack>
      <Stack
        direction="row"
        sx={{
          gap: 1,
        }}
      >
        <CerButtons
          cer={cer}
          onEdit={onEdit}
          handleDelete={onDelete}
          getPdf={getPdf}
          loading={pdfLoading}
          onLoadDocument={onLoadDocument}
          loadingUpload={loadingUpload}
          transferToDraft={transferToDraft}
        />
        {cer.document &&
          (pdfLoading ? (
            <CircularProgress size={24} sx={{ m: 1 }} />
          ) : (
            <IconButton onClick={() => getPdf("display")} color="primary">
              <RemoveRedEyeRounded className={classes.icon} />
            </IconButton>
          ))}
      </Stack>
    </CerCardStyled>
  );
};
